import type { CardData, CharacterDto, ProjectDto, ShotData, ShotDto } from '@studio/shared';
import { Download, Images } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

/**
 * The storyboard as one sheet, like a director's board: numbered panels with a "SCENE:" title, the still, the
 * speaker's line in a dark box over the picture, and the shot note underneath. Downloadable as one PNG to share.
 */

interface Panel {
  n: number;
  title: string;
  image: string | null;
  speaker: string;
  line: string;
  note: string;
  card: CardData | null;
}

export function panelsOf(project: ProjectDto, characters: CharacterDto[]): Panel[] {
  const names = new Map(characters.map((c) => [c.id, c.callName || c.name]));
  for (const c of project.cast) names.set(c.id, c.callName || c.name);
  return project.shots.map((s: ShotDto, i) => {
    if (s.kind === 'card') {
      const d = s.data as CardData;
      return { n: i + 1, title: 'TIME JUMP', image: null, speaker: '', line: '', note: 'Text card', card: d };
    }
    const d = s.data as ShotData;
    const first = d.lines.find((l) => l.text.trim());
    const speaker = first ? (first.characterId ? (names.get(first.characterId) ?? 'Voice') : first.speakerName || 'Voice') : '';
    const note = (d.camera || d.picture).replace(/\[([^\]]+)\]/g, '$1').replace(/\{dialogue\}/gi, '').replace(/\s+/g, ' ').trim();
    return {
      n: i + 1,
      title: (d.label || `Shot ${i + 1}`).toUpperCase(),
      image: s.frame?.url ?? null,
      speaker,
      line: first?.text.trim() ?? '',
      note: note.length > 110 ? `${note.slice(0, 107)}…` : note,
      card: null,
    };
  });
}

export function StoryboardSheet({ project, characters }: { project: ProjectDto; characters: CharacterDto[] }) {
  const panels = panelsOf(project, characters);
  const [saving, setSaving] = useState(false);
  const wide = project.aspect === '16:9';
  const download = async () => {
    setSaving(true);
    try {
      const blob = await drawSheet(project.title, panels, wide ? 16 / 9 : project.aspect === '1:1' ? 1 : 9 / 16);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${project.title.replace(/[^\w\- ]+/g, '').trim() || 'storyboard'} - storyboard.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch (err) {
      toast.error(`Could not make the sheet: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="mr-auto text-sm text-muted-foreground">
          The whole story on one page — share it before making videos.
        </span>
        <Button size="sm" onClick={() => void download()} loading={saving} disabled={!panels.length}>
          <Download /> Download sheet (PNG)
        </Button>
      </div>
      <div
        className={`grid gap-1.5 rounded-lg bg-neutral-950 p-1.5 ${
          wide ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
        }`}
      >
        {panels.map((p) => (
          <div key={p.n} className="overflow-hidden rounded-md bg-neutral-900 text-white">
            <div className="flex items-center gap-1.5 bg-black px-1.5 py-1">
              <span className="grid size-5 shrink-0 place-items-center rounded-sm bg-amber-400 text-xs font-extrabold text-black">
                {p.n}
              </span>
              <span className="truncate text-[11px] font-bold tracking-wide">SCENE: {p.title}</span>
            </div>
            <div className={`relative ${wide ? 'aspect-video' : project.aspect === '1:1' ? 'aspect-square' : 'aspect-[9/16]'}`}>
              {p.card ? (
                <div className="flex size-full items-center justify-center bg-amber-50 p-3 text-center">
                  <span className="text-2xl font-black whitespace-pre-line" style={{ color: p.card.textColor }}>
                    {p.card.text}
                  </span>
                </div>
              ) : p.image ? (
                <img src={p.image} alt="" className="size-full object-cover" />
              ) : (
                <div className="flex size-full flex-col items-center justify-center gap-1 text-xs text-neutral-400">
                  <Images className="size-6" /> no still yet
                </div>
              )}
              {p.line && (
                <div className="absolute inset-x-1.5 bottom-1.5 rounded bg-black/75 p-1.5 text-[11px] leading-snug">
                  <div className="font-bold text-amber-300">{p.speaker}:</div>
                  <div className="hindi line-clamp-4">{p.line}</div>
                </div>
              )}
            </div>
            <div className="line-clamp-2 bg-black px-1.5 py-1 text-[10px] text-neutral-300">Shot: {p.note}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── PNG export (canvas) ────────────────────────────────────────────────────

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => resolve(null);
    im.src = src;
  });
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = next;
  }
  if (line) lines.push(line);
  return lines;
}

async function drawSheet(title: string, panels: Panel[], aspect: number): Promise<Blob> {
  const cols = aspect < 1 ? Math.min(5, Math.max(3, Math.ceil(Math.sqrt(panels.length * 1.6)))) : 3;
  const pw = aspect < 1 ? 420 : 640;
  const ih = Math.round(pw / aspect);
  const head = 44;
  const foot = 52;
  const gap = 10;
  const ph = head + ih + foot;
  const rows = Math.ceil(panels.length / cols);
  const top = 90;
  const W = cols * pw + (cols + 1) * gap;
  const H = top + rows * ph + (rows + 1) * gap;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const x = c.getContext('2d')!;
  const font = (w: number, s: number) => `${w} ${s}px "Segoe UI", "Nirmala UI", "Noto Sans Devanagari", Arial, sans-serif`;
  x.fillStyle = '#0b0b0b';
  x.fillRect(0, 0, W, H);
  x.fillStyle = '#ffffff';
  x.font = font(800, 40);
  x.fillText(`${title} — Storyboard`, gap + 6, 58);
  const images = await Promise.all(panels.map((p) => (p.image ? loadImage(p.image) : Promise.resolve(null))));
  panels.forEach((p, i) => {
    const px = gap + (i % cols) * (pw + gap);
    const py = top + gap + Math.floor(i / cols) * (ph + gap);
    // Title bar.
    x.fillStyle = '#000000';
    x.fillRect(px, py, pw, head);
    x.fillStyle = '#fbbf24';
    x.fillRect(px + 8, py + 8, 28, 28);
    x.fillStyle = '#000000';
    x.font = font(900, 18);
    x.textAlign = 'center';
    x.fillText(String(p.n), px + 22, py + 29);
    x.textAlign = 'left';
    x.fillStyle = '#ffffff';
    x.font = font(800, 17);
    x.fillText(`SCENE: ${p.title}`.slice(0, 44), px + 44, py + 29);
    // Picture.
    const iy = py + head;
    const im = images[i];
    if (p.card) {
      x.fillStyle = '#fef3c7';
      x.fillRect(px, iy, pw, ih);
      x.fillStyle = p.card.textColor;
      x.font = font(900, Math.round(pw / 7));
      x.textAlign = 'center';
      const rowsT = p.card.text.split('\n');
      rowsT.forEach((r, k) => x.fillText(r, px + pw / 2, iy + ih / 2 + (k - (rowsT.length - 1) / 2) * (pw / 6.2)));
      x.textAlign = 'left';
    } else if (im) {
      // cover-fit
      const s = Math.max(pw / im.width, ih / im.height);
      const sw = pw / s;
      const sh = ih / s;
      x.drawImage(im, (im.width - sw) / 2, (im.height - sh) / 2, sw, sh, px, iy, pw, ih);
    } else {
      x.fillStyle = '#262626';
      x.fillRect(px, iy, pw, ih);
      x.fillStyle = '#a3a3a3';
      x.font = font(600, 18);
      x.textAlign = 'center';
      x.fillText('no still yet', px + pw / 2, iy + ih / 2);
      x.textAlign = 'left';
    }
    // Dialogue box.
    if (p.line) {
      x.font = font(600, 17);
      const lines = wrap(x, p.line, pw - 40).slice(0, 4);
      const bh = 34 + lines.length * 23;
      const by = iy + ih - bh - 10;
      x.fillStyle = 'rgba(0,0,0,0.75)';
      x.fillRect(px + 10, by, pw - 20, bh);
      x.fillStyle = '#fcd34d';
      x.font = font(800, 17);
      x.fillText(`${p.speaker}:`, px + 20, by + 24);
      x.fillStyle = '#ffffff';
      x.font = font(600, 17);
      lines.forEach((l, k) => x.fillText(l, px + 20, by + 48 + k * 23));
    }
    // Shot note.
    x.fillStyle = '#000000';
    x.fillRect(px, iy + ih, pw, foot);
    x.fillStyle = '#d4d4d4';
    x.font = font(500, 14);
    wrap(x, `Shot: ${p.note}`, pw - 16)
      .slice(0, 2)
      .forEach((l, k) => x.fillText(l, px + 8, iy + ih + 21 + k * 19));
  });
  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('empty'))), 'image/png'));
}
